'use client';

import { createContext, type PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import type { Nullable } from '@libs/core';
import { DeliveryType, isLocaleCA, AUTOSHIP_DISCOUNT_BANNER_LOCAL_STORAGE_KEY } from '@libs/core';
import { useCart, useLocalStorage, usePageLayout } from '../hooks';
import useCartState from './utils/useCartState';
import { hasFulfillmentMethodInCart } from '@libs/common-next/utils';

interface AutoshipPercentageProps {
    readonly percentage?: number;
    readonly isBannerVisible: boolean;
    readonly hideBanner: () => void;
}

export const AutoshipPercentageContext = createContext<Nullable<AutoshipPercentageProps>>(undefined);

const initialPercentage = isLocaleCA() ? 15 : 35;

export function AutoshipPercentageProvider({ children }: PropsWithChildren) {
    const [percentage, setPercentage] = useState<number | undefined>(undefined);
    const [isBannerVisible, setIsBannerVisible] = useState(false);
    const { flags } = usePageLayout();
    const autoshipDiscountUpdatesEnabled = Boolean(flags?.autoshipDiscountUpdatesEnabled);
    const [wasBannerShown, setWasBannerShown] = useLocalStorage<Record<string, boolean>>(
        AUTOSHIP_DISCOUNT_BANNER_LOCAL_STORAGE_KEY
    );

    const { cart } = useCart();

    useEffect(() => {
        if (!cart) {
            return;
        }

        if (autoshipDiscountUpdatesEnabled) {
            const hasAutoshipInCart = hasFulfillmentMethodInCart(cart, DeliveryType.AUTOSHIP);

            if (!hasAutoshipInCart) {
                setIsBannerVisible(false);
            }

            const hasAutoshipOrders = cart?.hasAutoshipOrders;

            if (hasAutoshipInCart && hasAutoshipOrders) {
                setPercentage(5);

                if (cart?.id && !wasBannerShown?.[cart.id]) {
                    setIsBannerVisible(true);
                    setWasBannerShown({
                        ...wasBannerShown,
                        [cart.id]: true,
                    });
                }

                return;
            }

            if (hasAutoshipOrders) {
                setPercentage(5);
                return;
            }

            setPercentage(initialPercentage);
        } else {
            if (!cart?.hasAutoshipOrders) {
                setPercentage(initialPercentage);
            }
        }
    }, [autoshipDiscountUpdatesEnabled, cart]);

    const hideBanner = useCallback(() => {
        setIsBannerVisible(false);
    }, []);

    const contextValue = useMemo(
        () => ({
            percentage,
            isBannerVisible,
            hideBanner,
        }),
        [percentage, isBannerVisible, hideBanner]
    );

    return <AutoshipPercentageContext.Provider value={contextValue}>{children}</AutoshipPercentageContext.Provider>;
}

export { useCartState };
